const PREFIX='p040-care-transfer:';
const MAX_AGE=48*60*60*1000;
export function parseTransfer(hash,now=Date.now()) {
 const match=/^#p040-pflege=([a-z0-9-]{20,90})~([0-9:,]{3,320})$/.exec(hash);
 if(!match)return null;
 const issued=Number.parseInt(match[1].split('-')[0],36);
 if(!Number.isSafeInteger(issued)||issued>now+300000||now-issued>MAX_AGE)return null;
 const lines=match[2].split(',').map(pair=>{const m=/^([1-9][0-9]{0,15}):([1-5])$/.exec(pair);return m?{id:Number(m[1]),quantity:Number(m[2])}:null;});
 if(!lines.length||lines.length>8||lines.some(l=>!l||!Number.isSafeInteger(l.id))||new Set(lines.map(l=>l.id)).size!==lines.length)return null;
 return {id:match[1],lines,fingerprint:match[2],issued};
}
const sorted=value=>Array.isArray(value)?value.map(sorted):value&&typeof value==='object'?Object.fromEntries(Object.keys(value).sort().map(k=>[k,sorted(value[k])])):value;
export async function cartSnapshot(cart,hash) {
 if(!cart||!Array.isArray(cart.items))throw Error('invalid_cart');
 const rows=new Map();
 for(const item of cart.items){
  if(!Number.isSafeInteger(item.variant_id)||item.variant_id<1||!Number.isSafeInteger(item.quantity)||item.quantity<1)throw Error('invalid_line');
  // Store only a digest of line properties, never engraving, notes or account data.
  const properties=item.properties??{},plan=item.selling_plan_allocation?.selling_plan?.id??null;
  const identity=JSON.stringify(sorted({id:item.variant_id,properties,plan,parent:item.parent_relationship??null}));
  const signature=await hash(identity),key=signature;
  const plain=!Object.keys(properties).length&&plan===null&&!item.parent_relationship;
  const old=rows.get(key);if(old)old.quantity+=item.quantity;else rows.set(key,{signature,id:item.variant_id,quantity:item.quantity,plain});
 }
 return [...rows.values()];
}
export function transferResult(before,after,lines) {
 const preserved=before.every(b=>after.filter(a=>a.signature===b.signature).reduce((n,a)=>n+a.quantity,0)>=b.quantity);
 const deltas=lines.map(l=>({id:l.id,wanted:l.quantity,added:after.filter(a=>a.id===l.id&&a.plain).reduce((n,a)=>n+a.quantity,0)-before.filter(a=>a.id===l.id&&a.plain).reduce((n,a)=>n+a.quantity,0)}));
 return {complete:preserved&&deltas.every(l=>l.added>=l.wanted),preserved,anyAdded:deltas.some(l=>l.added>0)};
}
export async function runTransfer(transfer,dependencies) {
 const {storage,withLock,readCart,addItems,hash,now=Date.now}=dependencies;
 if(!withLock||!storage)return {status:'unavailable'};
 return withLock(async()=>{
  const key=PREFIX+transfer.id;
  let receipt;
  try{
   const existing=storage.getItem(key);
   if(existing){
    receipt=JSON.parse(existing);
    if(receipt.v!==1||receipt.fingerprint!==transfer.fingerprint||!Array.isArray(receipt.before)||!['pending','done','partial','rejected'].includes(receipt.phase))return {status:'unavailable'};
    if(receipt.phase!=='pending')return {status:receipt.phase,repeated:true};
   }else{
    // Expired transfer links are rejected before these receipts are discarded.
    const keys=[];for(let i=0;i<storage.length;i++){const k=storage.key(i);if(k?.startsWith(PREFIX))keys.push(k);}
    for(const k of keys){try{const entry=JSON.parse(storage.getItem(k));if(now()-entry.created>MAX_AGE+3600000)storage.removeItem(k);}catch{}}
    if(keys.length>200)return {status:'unavailable'};
    const before=await cartSnapshot(await readCart(),hash);
    receipt={v:1,fingerprint:transfer.fingerprint,phase:'pending',before,created:now()};
    storage.setItem(key,JSON.stringify(receipt));
    if(storage.getItem(key)!==JSON.stringify(receipt))return {status:'unavailable'};
   }
  }catch{return {status:'unavailable'};}
  let rejection=false;
  if(!receipt.attempted){
   // Persist this before sending: uncertain writes must never be repeated.
   receipt.attempted=true;
   try{storage.setItem(key,JSON.stringify(receipt));}catch{return {status:'unavailable'};}
   try{await addItems(transfer.lines);}catch(error){rejection=error?.definitive===true;}
  }
  let result;
  try{result=transferResult(receipt.before,await cartSnapshot(await readCart(),hash),transfer.lines);}catch{return {status:'uncertain'};}
  if(!result.preserved)return {status:'uncertain'};
  const status=result.complete?'done':rejection?(result.anyAdded?'partial':'rejected'):'pending';
  receipt.phase=status;
  try{storage.setItem(key,JSON.stringify(receipt));}catch{return {status:'uncertain'};}
  return {status:status==='pending'?'uncertain':status};
 });
}

async function boot() {
 const panel=document.getElementById('p040-care-cart');
 if(!panel||!location.hash.startsWith('#p040-pflege='))return;
 const transfer=parseTransfer(location.hash),status=panel.querySelector('[data-care-status]');
 panel.hidden=false;
 if(!transfer){status.textContent='Diese Pflegeauswahl ist nicht mehr gültig. Bitte öffne dein Haarprofil und stelle deine Auswahl erneut zusammen.';return;}
 status.textContent='Deine Pflegeauswahl wird zum vorhandenen Warenkorb hinzugefügt …';
 panel.setAttribute('aria-busy','true');
 let storage;try{storage=localStorage;}catch{}
 const route=panel.dataset.cartRoot.replace(/\/?$/,'/');
 const request=async(path,body)=>{
  const controller=new AbortController();let timer;
  try{return await Promise.race([
   (async()=>{const response=await fetch(route+path,{method:body?'POST':'GET',credentials:'same-origin',cache:'no-store',headers:{'Content-Type':'application/json',Accept:'application/json'},...(body?{body:JSON.stringify(body)}:{}),signal:controller.signal});if(!response.ok){const error=new Error('cart_request');error.definitive=[400,404,422].includes(response.status);throw error;}return response.json();})(),
   new Promise((_,reject)=>{timer=setTimeout(()=>{controller.abort();reject(Error('timeout'));},20000);})
  ]);}finally{clearTimeout(timer);}
 };
 const hash=async value=>Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(value)))).map(n=>n.toString(16).padStart(2,'0')).join('');
 let result;
 try{result=await runTransfer(transfer,{storage,hash,withLock:navigator.locks?fn=>navigator.locks.request('p040-care-cart',{mode:'exclusive'},fn):null,readCart:()=>request('cart.js'),addItems:items=>request('cart/add.js',{items})});}catch{result={status:'uncertain'};}
 panel.setAttribute('aria-busy','false');
 const messages={done:'Deine Pflegeauswahl wurde hinzugefügt. Vorhandene Artikel bleiben im Warenkorb.',partial:'Ein Teil deiner Auswahl konnte nicht in der gewünschten Menge hinzugefügt werden. Bitte prüfe die Produkte und Mengen im Warenkorb.',rejected:'Deine Auswahl konnte nicht hinzugefügt werden. Ein Produkt oder die gewünschte Menge ist möglicherweise nicht mehr verfügbar. Deine vorhandenen Artikel bleiben erhalten.',uncertain:'Die Übernahme ist noch nicht vollständig bestätigt. Bitte prüfe deinen Warenkorb. Diese Auswahl wird nicht automatisch erneut hinzugefügt.',unavailable:'Die Auswahl kann hier gerade nicht sicher übernommen werden. Bitte prüfe deinen Warenkorb und öffne bei Bedarf dein Haarprofil erneut.'};
 status.textContent=result.repeated&&result.status==='done'?'Diese Auswahl wurde bereits hinzugefügt. Dein Warenkorb wird nicht erneut ergänzt.':messages[result.status]??messages.uncertain;
 panel.querySelector('[data-care-refresh]').hidden=false;
 panel.querySelector('[data-care-refresh]').addEventListener('click',()=>location.reload());
 if(!result.repeated&&['done','partial','rejected'].includes(result.status))location.reload();
}
if(typeof document!=='undefined')void boot();
